import { Network, ChevronDown } from 'lucide-react';
import { Control, useWatch } from 'react-hook-form';
import { FormField, FormItem, FormControl } from '@/components/ui/form';
import { cn } from '@/lib/utils';
import { EMPTY_GUID } from '@/lib/constants';
import { useState } from 'react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import BridgeRouteSelector from './BridgeRouteSelector';

interface SiteEdgeLinkSettingsProps {
  control: Control<any>;
  name: string;
}

// Helper to check if a route value is valid (not empty, not Guid.Empty)
const isValidRoute = (value: string | null | undefined): boolean =>
  Boolean(value && value !== EMPTY_GUID);

export function SiteEdgeLinkSettings({
  control,
  name,
}: SiteEdgeLinkSettingsProps) {
  const routeValue = useWatch({ control, name });
  const hasExistingRoute = isValidRoute(routeValue);

  const [isOpen, setIsOpen] = useState(hasExistingRoute);

  // Keep open if route is selected, otherwise allow toggle
  const handleOpenChange = (open: boolean) => {
    if (hasExistingRoute) return; // Can't collapse when route is selected
    setIsOpen(open);
  };

  return (
    <Collapsible open={isOpen || hasExistingRoute} onOpenChange={handleOpenChange}>
      <div className="rounded-xl border bg-[#111] p-6">
        <CollapsibleTrigger asChild disabled={hasExistingRoute}>
          <button
            type="button"
            className={cn(
              "flex w-full items-center justify-between text-left transition-colors",
              !hasExistingRoute && "cursor-pointer"
            )}
          >
            <div className="flex items-center gap-3">
              <Network className={cn(
                "h-5 w-5 transition-colors",
                hasExistingRoute ? "text-lime" : "text-muted-foreground"
              )} />
              <div className="flex flex-col">
                <span className="text-xl font-semibold">EdgeLink Default Route</span>
                <span className="text-gray-400">
                  {hasExistingRoute ? "Default route configured for this site" : "Set a default route for new devices at this site"}
                </span>
              </div>
            </div>
            {!hasExistingRoute && (
              <ChevronDown
                className={cn(
                  "h-4 w-4 text-muted-foreground transition-transform duration-200",
                  isOpen && "rotate-180"
                )}
              />
            )}
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="mt-6">
            <FormField
              control={control}
              name={name}
              render={({ field }) => {
                const handleChange = (routeId: string | null) => {
                  // If routeId is null (cleared), send Guid.Empty
                  if (routeId === null) {
                    field.onChange(EMPTY_GUID);
                  } else {
                    field.onChange(routeId);
                  }
                };

                return (
                  <FormItem>
                    <FormControl>
                      <BridgeRouteSelector
                        value={field.value}
                        onChange={handleChange}
                        placeholder="Select a default route..."
                      />
                    </FormControl>
                  </FormItem>
                );
              }}
            />
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

export default SiteEdgeLinkSettings;
